<template>

    <Toast/>

    <MenuFinancialCompact v-if="userStore.user?.roles?.includes('ROLE_ISORISE')" />
    <MenuHealthCompact v-else />

    <div class="px-6 surface-overlay relative">

        <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
            <li class="pr-3"><a class="cursor-pointer">Operational Clarity</a></li>
            <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
            <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Files</a></li>
        </ul>

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Manage Files</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callUploadFile" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>Upload File</span>
                                </a>
                            </li>
                        </ul>

                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callAddFile" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>Add External File</span>
                                </a>
                            </li>
                        </ul>

                        <ul v-if="isSupportSharePoint()" class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callShowSharePoint" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>Share Point Files</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DataTable
                        :value="storageRecords"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['id', 'startDate', 'endDate', 'productName']">

                        <template #header>
                            <div class="p-grid">
                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedColumns" :options="columnsFullList"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleColumns"
                                                     display="chip" placeholder="Select Columns"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedStandardFilter" :options="standardFilters"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleStandardFilters"
                                                     display="chip" placeholder="Select Standard Filter"/>
                                    </div>
                                </div>

                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                class="p-button-outlined p-ml-2" @click="clearFilter()"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-replay" label="Restore to Original View"
                                                class="p-button-outlined" @click="restoreToOriginal()"/>
                                    </div>
                                </div>

                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on ID, Start Date, End Date or Product Name"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No files found.
                        </template>

                        <template #loading>
                            Loading files. Please wait.
                        </template>

                        <Column
                            field="selectAnOption"
                            header=""
                            :headerStyle="{ width: '2rem' }"
                            :bodyStyle="{ width: '2rem', padding: '0.25rem' }">
                            <template #body="{ data, index }">
                                <Button icon="pi pi-ellipsis-v" class="p-button-sm p-button-text p-button-rounded m-0"
                                        @click="$refs['filesMenu' + index].toggle($event)"/>
                                <Menu
                                    :ref="'filesMenu' + index"
                                    :model="getFilteredItems(data)"
                                    :popup="true"/>
                            </template>
                        </Column>

                        <template v-for="col in selectedColumns" :key="col.field">

                            <Column v-if="col.field === 'type'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Type</span>
                                    <span v-if="data.type === 'G'"
                                          :class="'type-badge status-' + data.type">General</span>
                                    <span v-if="data.type === 'C'"
                                          :class="'type-badge status-' + data.type">Client</span>
                                    <span v-if="data.type === 'N'"
                                          :class="'type-badge status-' + data.type">Note</span>
                                    <span v-if="data.type === 'F'"
                                          :class="'type-badge status-' + data.type">Financial</span>
                                    <span v-if="data.type === 'T'"
                                          :class="'type-badge status-' + data.type">Task</span>
                                </template>
                            </Column>

                            <Column v-else-if="col.field === 'provider'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Provider</span>
                                    <span v-if="data.provider === 'E'"
                                          :class="'type-badge status-' + data.provider">External</span>
                                    <span v-else
                                          :class="'type-badge status-' + data.provider">Internal</span>
                                </template>
                            </Column>

                            <Column v-else-if="col.field === 'status'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Status</span>
                                    <span v-if="data.status === 'I'"
                                          :class="'status-badge status-' + data.status">Initiated</span>
                                    <span v-if="data.status === 'P'"
                                          :class="'status-badge status-' + data.status">Paid</span>
                                    <span v-if="data.status === 'C'"
                                          :class="'status-badge status-' + data.status">Completed</span>
                                </template>
                            </Column>

                            <Column v-else :field="col.field" :header="col.header" :sortable="col.sortable"/>

                        </template>


                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete File {{ this.storageRecord.simpleFilename }}</h3>

                    <div class="field">
                        <label for="confirmationResponse">Do You Wish to Delete this File?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes" optionLabel="name"
                                  optionValue="code" placeholder="Select a Response"/>
                    </div>


                    <Button label="Delete" class="mr-2 mb-2" :loading="submitted" @click="deleteStorageRecord"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>


        <div v-show="activeTab === 3" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Upload File</h3>

                    <div class="p-field">
                        <input
                            type="file"
                            ref="fileInput"
                            @change="handleFileUpload"
                            accept="application/pdf,image/png,image/jpeg"/>
                    </div>

                    <br/>

                    <div class="p-field">
                        <Button label="Upload" :loading="submitted" class="mr-2 mb-2" :disabled="!file"
                                @click="uploadFile"></Button>
                    </div>

                    <br/>
                    <div class="p-field">
                        <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>
                    </div>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 4" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Add External File</h3>

                    <div class="field">
                        <label for="externalFilename">External Filename</label>
                        <InputText id="externalFilename" v-model.trim="externalFilename" required="true"/>
                    </div>

                    <div class="field">
                        <label for="externalLink">Full Link to External File</label>
                        <InputText id="externalLink" v-model.trim="externalLink" required="true"/>
                    </div>

                    <Button label="Add File" class="mr-2 mb-2" :loading="submitted" @click="addFile"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 5" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update File</h3>

                    <div class="field">
                        <label for="externalFilename">Filename</label>
                        <InputText id="externalFilename" v-model.trim="storageRecord.simpleFilename" required="true"/>
                    </div>

                    <Button label="Update File" class="mr-2 mb-2" :loading="submitted" @click="updateFile"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 6" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update External File</h3>

                    <div class="field">
                        <label for="externalFilename">External Filename</label>
                        <InputText id="externalFilename" v-model.trim="storageRecord.simpleFilename" required="true"/>
                    </div>

                    <div class="field">
                        <label for="externalLink">Full Link to External File</label>
                        <InputText id="externalLink" v-model.trim="storageRecord.filename" required="true"/>
                    </div>

                    <Button label="Update File" class="mr-2 mb-2" :loading="submitted" @click="updateFile"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" :loading="submitted" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 7" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Share Point Files</h3>

                    <DataTable
                        :value="sharePointDriveItems"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="sharePointFilters"
                        filterDisplay="menu"
                        :loading="sharePointLoading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['id', 'name']">

                        <template #header>
                            <div class="p-grid">
                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="sharePointSelectedColumns" :options="sharePointColumnsFullList"
                                                     optionLabel="header"
                                                     @update:modelValue="sharePointOnToggleColumns"
                                                     display="chip" placeholder="Select Columns"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="sharePointSelectedStandardFilter" :options="sharePointStandardFilters"
                                                     optionLabel="header"
                                                     @update:modelValue="sharePointOnToggleStandardFilters"
                                                     display="chip" placeholder="Select Standard Filter"/>
                                    </div>
                                </div>

                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                class="p-button-outlined p-ml-2" @click="sharePointClearFilter()"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-replay" label="Restore to Original View"
                                                class="p-button-outlined" @click="sharePointRestoreToOriginal()"/>
                                    </div>
                                </div>

                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="sharePointFilters['global'].value"
                                                   placeholder="Keyword Search on ID or Name"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No files found.
                        </template>

                        <template #loading>
                            Loading files. Please wait.
                        </template>

                        <template v-for="col in sharePointSelectedColumns" :key="col.field">
                            <Column v-if="col.field === 'name'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Url</span>
                                    <a :href="data.webUrl" target="_blank" rel="noopener noreferrer">{{data.name}}</a>
                                </template>
                            </Column>

                            <Column v-else :field="col.field" :header="col.header" :sortable="col.sortable"/>
                        </template>

                    </DataTable>
                </div>
            </div>
        </div>
    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import CompanyService from "@/service/CompanyService";
import StorageService from "@/service/StorageService";
import {FilterMatchMode, FilterOperator} from "primevue/api";
import axios from "axios";
import MenuFinancialCompact from "@/components/menu/famodule/MenuFinancialCompact.vue";

export default {
    components: {
        MenuFinancialCompact,
        IsoEvolveFooter, MenuHealthCompact
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    created() {
        this.initFilters();
        this.sharePointInitFilters();

        this.columnsDefault = [
            {field: 'type', header: 'Type', sortable: true},
            {field: 'provider', header: 'Provider', sortable: true},
            {field: 'simpleFilename', header: 'Name', sortable: true},
            {field: 'clientName', header: 'Client Name', sortable: true},
            {field: 'created', header: 'Created', sortable: true}
        ];

        this.columnsFullList = [
            {field: 'type', header: 'Type', sortable: true},
            {field: 'provider', header: 'Provider', sortable: true},
            {field: 'simpleFilename', header: 'Name', sortable: true},
            {field: 'clientName', header: 'Client Name', sortable: true},
            {field: 'created', header: 'Created', sortable: true}
        ];

        this.selectedColumns = this.columnsDefault;

        this.standardFilters = [
            {field: 'general', header: 'General'},
            {field: 'client', header: 'Client'},
            {field: 'note', header: 'Note'},
            {field: 'task', header: 'Task'}
        ];

        // Share point table variables
        this.sharePointColumnsDefault = [
            {field: 'type', header: 'Type', sortable: true},
            {field: 'name', header: 'Name', sortable: true},
            {field: 'size', header: 'Size', sortable: true},
            {field: 'createdDateTime', header: 'Created', sortable: true}
        ];

        this.sharePointColumnsFullList = [
            {field: 'type', header: 'Type', sortable: true},
            {field: 'name', header: 'Name', sortable: true},
            {field: 'size', header: 'Size', sortable: true},
            {field: 'createdDateTime', header: 'Created', sortable: true}
        ];

        this.sharePointSelectedColumns = this.sharePointColumnsDefault;

        this.sharePointStandardFilters = [
            {field: 'general', header: 'General'}
        ];
    },
    async mounted() {

        await this.loadCompanyContext();

    },
    data() {

        return {
            profile: {},
            externalFilename: null,
            externalLink: null,
            companyConfiguration: {},
            activeTab: 1,
            storageRecords: null,
            loading: true,
            submitted: false,
            filters: null,
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            storageRecord: {},
            sharePointLoading: true,
            sharePointFilters: null,
            sharePointDriveItems: null,
            sharePointColumnsDefault: null,
            sharePointColumnsFullList: null,
            sharePointStandardFilters: null,
            sharePointSelectedColumns: null,
            sharePointSelectedStandardFilter: null,
            confirmationResponse: 'N',
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            file: null,
            url: "/storage/1",
            fileAccept: 'application/pdf',
            selectedFileType: 'G',
            fileTypes: [
                {name: 'General', code: 'G'},
                {name: 'Client', code: 'C'},
                {name: 'Task', code: 'T'},
                {name: 'Note', code: 'N'},
                {name: 'Prospect', code: 'P'}
            ],

        }
    },
    watch: {
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                console.log(
                    '[Appointments][watch] Company changed:',
                    oldCompanyId,
                    '→',
                    newCompanyId
                );

                await this.loadCompanyContext();
            }
        }
    },
    methods: {
        async loadCompanyContext() {
            this.loading = true;

            try {
                // Attempt to get user data
                const userData = await UserService.getUser(this.userStore.user.id);
                this.profile = userData.data;
            } catch (e) {
                console.error("[AppointmentsNew][mounted] Error fetching user data = ", e);
                this.userStore.clearUser();
                this.$router.push('login');
                return;
            }

            this.profile.companyId = this.userStore.activeCompanyId;

            try {
                const [companyConfigData, storageData] = await Promise.all([
                    CompanyService.getCompanyConfiguration(this.profile.companyId),
                    StorageService.getCompanyStorage(this.profile.companyId),
                ]);

                this.companyConfiguration = companyConfigData.data;
                this.storageRecords = storageData.data;
                console.log("[OverviewFiles][mounted] companyConfigData.data = ", companyConfigData.data);
                console.log("[OverviewFiles][mounted] storageData.data = ", storageData.data);

                this.loading = false;

            } catch (e) {
                console.error("[OverviewFiles][mounted] Error fetching additional data = ", e);
            }

            if (this.$route.query.new === "1") {
                this.callUploadFile();
            }

            console.log("[OverviewFiles][mounted] this.companyConfiguration = ", this.companyConfiguration);
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'id': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'type': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'simpleFilename': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        clearFilter() {
            this.initFilters();
        },
        restoreToOriginal() {
            window.location.reload();
        },
        callReset() {
            this.activeTab = 1;
            this.resetFileInput();
            this.selectedFileType = null;
        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col => value.includes(col));
        },
        onToggleStandardFilters(value) {

            if (value.length > 1) {
                // Keep the last selected option
                this.selectedStandardFilter = [value[value.length - 1]];
            } else {
                this.selectedStandardFilter = value;
            }
        },
        async callDeleteStorageRecord(data) {
            this.activeTab = 2;
            this.confirmationResponse = 'N';
            this.storageRecord = data;
        },
        async callUpdateStorageRecord(data) {
            this.activeTab = 5;
            this.storageRecord = data;
        },
        async callUpdateExternalFile(data) {
            this.activeTab = 6;
            this.storageRecord = data;
        },
        getFilteredItems(data) {

            const items = [];

            if (data.provider === 'E') {
                items.push({
                    label: 'Update',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callUpdateExternalFile(data);
                    }
                });

                items.push({
                    label: 'View',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.viewExternalFile(data);
                    }
                });

                items.push({
                    label: 'Delete',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callDeleteStorageRecord(data);
                    }
                });

            } else {
                items.push({
                    label: 'Update',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callUpdateStorageRecord(data);
                    }
                });

                items.push({
                    label: 'View',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.viewFile(data);
                    }
                });

                items.push({
                    label: 'Download',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.downloadFile(data);
                    }
                });

                items.push({
                    label: 'Delete',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callDeleteStorageRecord(data);
                    }
                });
            }

            return items;
        },
        callUploadFile() {
            this.selectedFileType = 'G';
            this.activeTab = 3;
        },
        callAddFile() {
            this.selectedFileType = 'G';
            this.activeTab = 4;
        },
        isSupportSharePoint() {
            if (this.companyConfiguration.mailServerMode === 'M') {
                return true;
            } else {
                return false;
            }
        },
        async callShowSharePoint() {
            this.activeTab = 7;
            try {
                const [sharePointData] = await Promise.all([
                    StorageService.getCompanyFilesSharePoint(this.profile.companyId),
                ]);

                this.sharePointDriveItems = sharePointData.data;
                console.log("[OverviewFiles][mounted] sharePointData.data = ", sharePointData.data);

                this.sharePointLoading = false;
            } catch (e) {
                console.error("[OverviewFiles][mounted] Error fetching share point data = ", e);
            }
        },
        handleFileUpload(event) {
            const file = event.target.files[0];

            if (!file) {
                this.file = null;
                return;
            }

            const allowedTypes = [
                'application/pdf',
                'image/png',
                'image/jpeg'
            ];

            if (!allowedTypes.includes(file.type)) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Invalid file type',
                    detail: 'Only PDF, PNG, and JPG files are allowed.',
                    life: 3000
                });
                this.resetFileInput();
                return;
            }

            this.file = file;
        },
        resetFileInput() {
            this.file = null;
            this.$refs.fileInput.value = '';
        },
        async addFile() {
            this.submitted = true;

            var storageRecord = {
                companyId: this.profile.companyId,
                clientId: null,
                noteId: null,
                taskId: null,
                type: 'G',
                provider: 'E',
                contentType : 'application/pdf',
                simpleFilename: this.externalFilename,
                filename: this.externalLink
            };

            console.log('[OverviewFiles][addFile] storageRecord = ', storageRecord);

            await StorageService.addExternalFile(this.profile.companyId, storageRecord);

            await this.loadCompanyContext();

            this.externalFilename = null;
            this.externalLink = null;
            this.submitted = false;
            this.activeTab = 1;
        },
        async updateFile() {
            this.submitted = true;

            console.log('[OverviewFiles][updateFile] this.storageRecord = ', this.storageRecord);

            await StorageService.updateCompanyStorage(this.storageRecord.id, this.storageRecord);

            await this.loadCompanyContext();

            this.submitted = false;
            this.activeTab = 1;
        },
        async uploadFile() {
            this.submitted = true;

            if (!this.file) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No file selected.',
                    life: 3000
                });
            }

            console.log('[OverviewFiles][uploadFile] this.selectedFileType = ', this.selectedFileType);

            const formData = new FormData();
            formData.append('file', this.file);

            const originalName = this.file.name;
            const filenameWithoutExt = originalName.substring(0, originalName.lastIndexOf('.')) || originalName;

            const storageRecord = {
                companyId: this.profile.companyId,
                clientId: null,
                noteId: null,
                taskId: null,
                type: 'G',
                provider: 'H',
                simpleFilename: filenameWithoutExt,
                filename: filenameWithoutExt,
                contentType: this.file.type
            };

            formData.append('storageRecord', new Blob([JSON.stringify(storageRecord)], {type: 'application/json'}));

            try {
                const response = await axios.post('/api/storage/hetzner/upload', formData, {
                    headers: {
                        'Authorization': 'Bearer ' + this.userStore.accessToken,
                        'Content-Type': 'multipart/form-data'
                    }
                });

                console.log('[OverviewFiles][uploadFile] Upload successful:', response.data);

                const [storageData] = await Promise.all([
                    StorageService.getCompanyStorage(this.profile.companyId),
                ]);

                this.storageRecords = storageData.data;

                this.$toast.add({
                    severity: 'success',
                    summary: 'Successful',
                    detail: 'File uploaded successfully',
                    life: 3000
                });

            } catch (error) {
                console.error('Error uploading file:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Upload Failed',
                    detail: 'File upload NOT successful',
                    life: 3000
                });
            }

            this.resetFileInput();
            this.submitted = false;
            this.activeTab = 1;
        },
        async deleteStorageRecord() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.storageRecord.id) {

                    try {

                        await StorageService.deleteCompanyStorage(this.storageRecord.id);

                        const [storageData] = await Promise.all([
                            StorageService.getCompanyStorage(this.profile.companyId),
                        ]);

                        this.storageRecords = storageData.data;

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted file',
                            life: 3000
                        });

                    } catch (error) {
                        console.error('[OverviewFiles][deleteStorageRecord] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete file. Please try again.',
                            life: 3000
                        });
                    }
                }
            }

            this.activeTab = 1;
            this.submitted = false;

        },
        async viewExternalFile(data) {
            try {
                this.storageRecord = data;

                if (this.storageRecord.filename) {
                    window.open(
                        this.storageRecord.filename,
                        '_blank',
                        'noopener,noreferrer'
                    );
                }
            } catch (error) {
                console.error('[OverviewFiles][viewExternalFile] Error:', error);
            }
        },
        async viewFile(data) {
            try {
                this.storageRecord = data;

                const [response] = await Promise.all([
                    StorageService.downloadFile(this.profile.companyId, this.storageRecord.id)
                ]);

                const blob = new Blob(
                    [response.data],
                    {type: this.storageRecord.contentType}
                );

                const url = URL.createObjectURL(blob);
                window.open(url);

            } catch (error) {
                console.error('[OverviewFiles][viewFile] Error:', error);
            }
        },
        async downloadFile(data) {
            try {
                this.storageRecord = data;

                const [responseData] = await Promise.all([
                    StorageService.downloadFile(this.profile.companyId, this.storageRecord.id)
                ]);

                console.log('[OverviewFiles][downloadFile] responseData = ', responseData);
                console.log('[OverviewFiles][downloadFile] this.storageRecord = ', this.storageRecord);

                const fullFilename = this.storageRecord.filename;
                const displayName = fullFilename.includes('~')
                    ? fullFilename.substring(fullFilename.lastIndexOf('~') + 1)
                    : fullFilename;

                // Use the correct content type from the storage record
                const contentType = this.storageRecord.contentType || 'application/octet-stream';

                const blob = new Blob([responseData.data], { type: contentType });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', displayName);
                document.body.appendChild(link);
                link.click();

                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);

                this.$toast.add({
                    severity: 'success',
                    summary: 'Successful',
                    detail: 'Downloaded file',
                    life: 3000
                });

            } catch (error) {
                console.error('[OverviewFiles][downloadFile] Error:', error);

                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to download file. Please try again.',
                    life: 3000
                });
            }
        },
        sharePointInitFilters() {
            this.sharePointFilters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'id': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'name': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        sharePointClearFilter() {
            this.sharePointInitFilters();
        },
        sharePointRestoreToOriginal() {
            window.location.reload();
        },
        sharePointOnToggleColumns(value) {
            this.sharePointSelectedColumns = this.sharePointColumnsFullList.filter(col => value.includes(col));
        },
        sharePointOnToggleStandardFilters(value) {

            if (value.length > 1) {
                // Keep the last selected option
                this.sharePointSelectedStandardFilter = [value[value.length - 1]];
            } else {
                this.sharePointSelectedStandardFilter = value;
            }
        },
    }

};
</script>

<style scoped lang="scss">
a {
    text-decoration: none;
}

.type-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}

.type-badge {
    &.status-P {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-N {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-S {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-B {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-C {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-U {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-P {
        background: #ECCFFF;
        color: #694382;
    }

    &.status-O {
        background: #ECCFFF;
        color: #c75226;
    }

    &.status-F {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-R {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-E {
        background: #ECCFFF;
        color: #694382;
    }

    &.status-G {
        background: #ECCFFF;
        color: #c75226;
    }

    &.status-H {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-A {
        background: #ECCFFF;
        color: #694382;
    }

    &.status-T {
        background: #C8E6C9;
        color: #256029;
    }
}
</style>
